import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LatLong } from 'record-it-utils/location/LocationUtils/LocationUtils';
import { Grid } from '../../grid/Grid/Grid';
import { GridItem } from '../../grid/GridItem/GridItem';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useModalLifecycleEffect } from '../../hooks/useModalLifecycleEffect/useModalLifecycleEffect';
import { BufferedNumberInput } from '../../input/NumberInput/NumberInput';
import { BasemapMap } from '../../map/BasemapMap/BasemapMap';
import { MapHandle } from '../../map/BasemapMap/MapController/MapController';
import { IconType } from '../../misc/IconDeprecated/IconDeprecated';
import { BaseDialogIconButton } from '../../modal/BaseDialogIconButton/BaseDialogIconButton';
import { BaseDialogTextButton, BaseDialogTextButtonType } from '../../modal/BaseDialogTextButton/BaseDialogTextButton';
import { SingleTabBaseDialog, SingleTabBaseDialogProps } from '../../modal/SingleTabBaseDialog/SingleTabBaseDialog';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { MarkerIcon } from '../MarkerIcon/MarkerIcon';
import styles from './SelectLocationDialog.module.css';
export type SelectLocationDialogProps = PropsWithoutChildren<Omit<SingleTabBaseDialogProps, 'title' | 'renderButtons' | 'fullHeight'>> & {
  location: LatLong | null;
  onAccept: (location: LatLong | null) => void;
  onCancel: () => void;
};
export const SelectLocationDialog: FunctionComponent<SelectLocationDialogProps> = React.memo(props => {
  const $ = _c(69);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let location;
  let onAccept;
  let onCancel;
  let opened;
  let remainingProps;
  if ($[2] !== t0) {
    ({
      location,
      opened,
      onAccept,
      onCancel,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = location;
    $[4] = onAccept;
    $[5] = onCancel;
    $[6] = opened;
    $[7] = remainingProps;
  } else {
    location = $[3];
    onAccept = $[4];
    onCancel = $[5];
    opened = $[6];
    remainingProps = $[7];
  }
  const [mapHandle, setMapHandle] = useState(null);
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      latitude: null,
      longitude: null
    };
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const [displayedCoordinates, setDisplayedCoordinates] = useState(t1);
  const {
    t
  } = useTranslation("record-it-components");
  let t2;
  if ($[9] !== displayedCoordinates.latitude || $[10] !== displayedCoordinates.longitude || $[11] !== onAccept) {
    t2 = () => {
      let newLocation = null;
      if (displayedCoordinates.latitude != null && displayedCoordinates.longitude != null) {
        newLocation = {
          latitude: displayedCoordinates.latitude,
          longitude: displayedCoordinates.longitude
        };
      }
      onAccept(newLocation);
    };
    $[9] = displayedCoordinates.latitude;
    $[10] = displayedCoordinates.longitude;
    $[11] = onAccept;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleAcceptClick = useEventCallback(t2);
  let t3;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = handle => {
      setDisplayedCoordinates({
        ...handle.getCenter()
      });
    };
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const handleMapMove = useEventCallback(t3);
  let t4;
  if ($[14] !== handleAcceptClick || $[15] !== onCancel || $[16] !== t) {
    t4 = () => [<BaseDialogTextButton key="cancel" type={BaseDialogTextButtonType.Secondary} onClick={onCancel} data-testid="SelectLocationDialog.CancelButton">{t("location.SelectLocationDialog.cancel")}</BaseDialogTextButton>, <BaseDialogIconButton key="accept" iconName="check" iconType={IconType.Regular} className={styles.acceptButton} onClick={handleAcceptClick} data-testid="SelectLocationDialog.AcceptButton" />];
    $[14] = handleAcceptClick;
    $[15] = onCancel;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const renderButtons = t4;
  const updateMapPosition = useEventCallback(_temp);
  let t5;
  if ($[18] !== displayedCoordinates.longitude || $[19] !== mapHandle || $[20] !== updateMapPosition) {
    t5 = latitude => {
      const latLong_0 = {
        longitude: displayedCoordinates.longitude,
        latitude
      };
      setDisplayedCoordinates(latLong_0);
      updateMapPosition(mapHandle, latLong_0);
    };
    $[18] = displayedCoordinates.longitude;
    $[19] = mapHandle;
    $[20] = updateMapPosition;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const handleLatitudeInput = useEventCallback(t5);
  let t6;
  if ($[22] !== displayedCoordinates.latitude || $[23] !== mapHandle || $[24] !== updateMapPosition) {
    t6 = longitude => {
      const latLong_1 = {
        longitude,
        latitude: displayedCoordinates.latitude
      };
      setDisplayedCoordinates(latLong_1);
      updateMapPosition(mapHandle, latLong_1);
    };
    $[22] = displayedCoordinates.latitude;
    $[23] = mapHandle;
    $[24] = updateMapPosition;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const handleLongitudeInput = useEventCallback(t6);
  let t7;
  if ($[26] !== displayedCoordinates || $[27] !== opened || $[28] !== updateMapPosition) {
    t7 = handle_1 => {
      setMapHandle(handle_1);
      if (opened) {
        updateMapPosition(handle_1, displayedCoordinates, false);
      }
    };
    $[26] = displayedCoordinates;
    $[27] = opened;
    $[28] = updateMapPosition;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const handleMapHandleRef = useEventCallback(t7);
  let t8;
  if ($[30] !== location?.latitude || $[31] !== location?.longitude) {
    t8 = () => {
      const latLong_2 = {
        latitude: location?.latitude ?? null,
        longitude: location?.longitude ?? null
      };
      setDisplayedCoordinates(latLong_2);
    };
    $[30] = location?.latitude;
    $[31] = location?.longitude;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== opened || $[34] !== t8) {
    t9 = {
      opened,
      afterOpened: t8
    };
    $[33] = opened;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  useModalLifecycleEffect(t9);
  let t10;
  if ($[36] !== t) {
    t10 = t("location.SelectLocationDialog.title");
    $[36] = t;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t) {
    t11 = t("location.SelectLocationDialog.latitude");
    $[38] = t;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== displayedCoordinates.latitude || $[41] !== handleLatitudeInput || $[42] !== t11) {
    t12 = <GridItem size={6}><BufferedNumberInput label={t11} value={displayedCoordinates.latitude} onInput={handleLatitudeInput} data-testid="SelectLocationDialog.LatitudeInput" /></GridItem>;
    $[40] = displayedCoordinates.latitude;
    $[41] = handleLatitudeInput;
    $[42] = t11;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== t) {
    t13 = t("location.SelectLocationDialog.longitude");
    $[44] = t;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== displayedCoordinates.longitude || $[47] !== handleLongitudeInput || $[48] !== t13) {
    t14 = <GridItem size={6}><BufferedNumberInput label={t13} value={displayedCoordinates.longitude} onInput={handleLongitudeInput} data-testid="SelectLocationDialog.LongitudeInput" /></GridItem>;
    $[46] = displayedCoordinates.longitude;
    $[47] = handleLongitudeInput;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== t12 || $[51] !== t14) {
    t15 = <Grid className={styles.latLongInputGrid}>{t12}{t14}</Grid>;
    $[50] = t12;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== handleMapHandleRef || $[54] !== handleMapMove) {
    t16 = <BasemapMap refHandle={handleMapHandleRef} onMapMove={handleMapMove} />;
    $[53] = handleMapHandleRef;
    $[54] = handleMapMove;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== displayedCoordinates.latitude || $[57] !== displayedCoordinates.longitude) {
    t17 = displayedCoordinates.latitude && displayedCoordinates.longitude ? <MarkerIcon className={styles.centerMarker} data-testid="SelectLocationDialog.CenterMarker" /> : null;
    $[56] = displayedCoordinates.latitude;
    $[57] = displayedCoordinates.longitude;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] !== t16 || $[60] !== t17) {
    t18 = <div className={styles.mapContainer}>{t16}{t17}</div>;
    $[59] = t16;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== opened || $[63] !== remainingProps || $[64] !== renderButtons || $[65] !== t10 || $[66] !== t15 || $[67] !== t18) {
    t19 = <SingleTabBaseDialog {...remainingProps} opened={opened} title={t10} fullHeight={true} renderButtons={renderButtons}>{t15}{t18}</SingleTabBaseDialog>;
    $[62] = opened;
    $[63] = remainingProps;
    $[64] = renderButtons;
    $[65] = t10;
    $[66] = t15;
    $[67] = t18;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  return t19;
});
type NullableLatLong = {
  latitude: number | null;
  longitude: number | null;
};
function _temp(handle_0, latLong, t0) {
  const animate = t0 === undefined ? true : t0;
  if (latLong.latitude != null && latLong.longitude != null) {
    handle_0?.setCenter({
      latitude: latLong.latitude,
      longitude: latLong.longitude
    }, {
      zoom: 18,
      animate
    });
  }
}